import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface AddBookingModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: any) => void;
	currency?: string;
}

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 999999;
	padding: 20px;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 5px;
	width: 500px;
	max-width: calc(100vw - 40px);
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	position: relative;
`;

const ModalHeader = styled.div`
	padding: 20px 20px 0 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ModalTitle = styled.h2`
	font-size: 18px;
	font-weight: 700;
	color: #111827;
	margin: 0;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 24px;
	color: #6B7280;
	cursor: pointer;
	padding: 4px;
	
	&:hover {
		color: #374151;
	}
`;

const ModalBody = styled.div`
	padding: 20px;
`;

const BookingTypeField = styled.div`
	background-color: #FEFDE9;
	border: 1px solid #E5E7EB;
	border-radius: 6px;
	padding: 12px;
	margin-bottom: 20px;
	font-size: 14px;
	color: #878787;
`;

const FormRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 16px;
`;

const FormGroup = styled.div`
	display: flex;
	flex-direction: column;
`;

const Label = styled.label`
	font-size: 14px;
	font-weight: 500;
	color: #000000;
	margin-bottom: 6px;
	
	&.required::after {
		content: ' *';
		color: #EF4444;
	}
`;

const Input = styled.input<{ hasValue?: boolean; hasError?: boolean }>`
	padding: 10px 12px;
	border: 1px solid ${props => props.hasError ? '#EF4444' : '#D1D5DB'};
	border-radius: 6px;
	font-size: 14px;
    width: 100%;
	color: ${props => props.hasValue ? '#111827' : '#878787'};
	
	&::placeholder {
		color: #878787;
	}
	
	&:focus {
		outline: none;
		border-color: ${props => props.hasError ? '#EF4444' : '#3B82F6'};
		box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
		color: #111827;
	}
`;

const DateInput = styled.div`
	position: relative;
`;

const ErrorMessage = styled.div`
	font-size: 12px;
	color: #EF4444;
	margin-top: 4px;
`;

const ModalFooter = styled.div`
	padding: 0 20px 20px 20px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const ActionButton = styled.button<{ variant: 'cancel' | 'submit' }>`
	padding: 10px 15px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	
	${props => {
		switch (props.variant) {
			case 'cancel':
                return `
					background: white;
					color: #111827;
					border: 1px solid #D1D5DB;
					
					&:hover {
						background: white;
					}
				`;
            case 'submit':
                return `
					background: #2563EB;
					color: white;
					border: 1px solid #2563EB;
					
					&:hover {
						background: #2563EB;
					}
				`;
        }
	}}
`;

export default function AddBookingModal({ 
	isOpen, 
	onClose, 
	onSubmit,
	currency = 'USD'
}: AddBookingModalProps) {
	const [formData, setFormData] = useState({
		customerName: '',
		email: '',
		dropoff: '',
		pickup: '',
		quantity: '1',
		amount: '0.00'
	});

	const [errors, setErrors] = useState<{ [key: string]: string }>({});

	const handleInputChange = (field: string, value: string) => {
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
		// Clear error khi user bắt đầu nhập
		if (errors[field]) {
			setErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const validateForm = (): boolean => {
		const newErrors: { [key: string]: string } = {};

		if (!formData.customerName || formData.customerName.trim() === '') {
			newErrors.customerName = 'Vui lòng nhập tên khách hàng';
		}

		if (!formData.email || formData.email.trim() === '') {
			newErrors.email = 'Vui lòng nhập email';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'Email không hợp lệ';
		}

		if (!formData.dropoff) {
			newErrors.dropoff = 'Vui lòng chọn ngày drop-off';
		}

		if (!formData.pickup) {
			newErrors.pickup = 'Vui lòng chọn ngày pick-up';
		}

		if (!formData.quantity || formData.quantity === '0' || parseInt(formData.quantity) < 1) {
			newErrors.quantity = 'Số lượng hành lý phải lớn hơn 0';
		}

		if (!formData.amount || formData.amount === '0' || formData.amount === '0.00' || parseFloat(formData.amount) <= 0) {
			newErrors.amount = 'Số tiền phải lớn hơn 0';
		}

		// Kiểm tra pick-up phải sau drop-off
		if (formData.dropoff && formData.pickup) {
			const dropoffDate = new Date(formData.dropoff);
			const pickupDate = new Date(formData.pickup);
			if (pickupDate <= dropoffDate) {
				newErrors.pickup = 'Ngày pick-up phải sau ngày drop-off';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		onSubmit(formData);
		onClose();
	};

	// Reset form khi modal đóng/mở
	useEffect(() => {
		if (isOpen) {
			setFormData({
				customerName: '',
				email: '',
				dropoff: '',
				pickup: '',
				quantity: '1',
				amount: '0.00'
			});
			setErrors({});
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<ModalTitle>Thêm Booking bổ sung</ModalTitle>
					<CloseButton onClick={onClose}>×</CloseButton>
				</ModalHeader>
				
				<ModalBody>
					<form onSubmit={handleSubmit}>
						<BookingTypeField>
							Booking bổ sung sau (mặc định)
						</BookingTypeField>
						
						<FormRow>
							<FormGroup>
								<Label className="required">Tên khách hàng</Label>
								<Input
									type="text"
									value={formData.customerName}
									onChange={(e) => handleInputChange('customerName', e.target.value)}
									hasValue={!!formData.customerName}
									hasError={!!errors.customerName}
									required
								/>
								{errors.customerName && <ErrorMessage>{errors.customerName}</ErrorMessage>}
							</FormGroup>
							<FormGroup>
								<Label className="required">Email</Label>
								<Input
									type="email"
									value={formData.email}
									onChange={(e) => handleInputChange('email', e.target.value)}
									hasValue={!!formData.email}
									hasError={!!errors.email}
									required
								/>
								{errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
							</FormGroup>
						</FormRow>
						
						<FormRow>
							<FormGroup>
								<Label className="required">Drop-off</Label>
								<DateInput>
									<Input
										type="date"
										value={formData.dropoff}
										onChange={(e) => handleInputChange('dropoff', e.target.value)}
										hasValue={!!formData.dropoff}
										hasError={!!errors.dropoff}
										required
									/>
								</DateInput>
								{errors.dropoff && <ErrorMessage>{errors.dropoff}</ErrorMessage>}
							</FormGroup>
							<FormGroup>
								<Label className="required">Pick-up</Label>
								<DateInput>
									<Input
										type="date"
										value={formData.pickup}
										onChange={(e) => handleInputChange('pickup', e.target.value)}
										hasValue={!!formData.pickup}
										hasError={!!errors.pickup}
										required
									/>
								</DateInput>
								{errors.pickup && <ErrorMessage>{errors.pickup}</ErrorMessage>}
							</FormGroup>
						</FormRow>
						
						<FormRow>
							<FormGroup>
								<Label className="required">Số lượng hành lý</Label>
								<Input
									type="number"
									min="1"
									value={formData.quantity}
									onChange={(e) => handleInputChange('quantity', e.target.value)}
									hasValue={!!formData.quantity && formData.quantity !== '0'}
									hasError={!!errors.quantity}
									required
								/>
								{errors.quantity && <ErrorMessage>{errors.quantity}</ErrorMessage>}
							</FormGroup>
							<FormGroup>
								<Label className="required">Số tiền ({currency})</Label>
								<Input
									type="number"
									step="0.01"
									value={formData.amount}
									onChange={(e) => handleInputChange('amount', e.target.value)}
									hasValue={!!formData.amount && formData.amount !== '0.00' && formData.amount !== '0'}
									hasError={!!errors.amount}
									required
								/>
								{errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
							</FormGroup>
						</FormRow>
					</form>
				</ModalBody>
				
				<ModalFooter>
					<ActionButton type="button" variant="cancel" onClick={onClose}>
						Hủy
					</ActionButton>
					<ActionButton type="submit" variant="submit" onClick={handleSubmit}>
						Thêm Booking
					</ActionButton>
				</ModalFooter>
			</ModalContent>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
